import {Separator} from 'platform/components';
import {VStack} from 'platform/foundation';

import i18n from '../../../../../i18n/i18n';
import {RelativeFilterChoice, RelativeFilterConfigBody} from '../../../../../types/api/api';
import {SeparatorWithLabel} from '../../../../SeparatorWithLabel';
import {ThreeWayPropertyChips} from './ThreeWayPropertyChips';
import {TwoWayPropertyChips} from './TwoWayPropertyChips';

export type PropertiesSlice = Pick<
  RelativeFilterConfigBody,
  | 'trimLevel'
  | 'transmission'
  | 'fuel'
  | 'body'
  | 'drive'
  | 'vatDeductible'
  | 'sellerType'
  | 'seats'
>;

interface PropertiesSectionProps {
  value: PropertiesSlice;
  onChange: (next: PropertiesSlice) => void;
  isReadOnly?: boolean;
}

export function PropertiesSection(props: PropertiesSectionProps) {
  const {value, onChange, isReadOnly} = props;

  const set =
    <K extends keyof typeof value>(key: K) =>
    (next: (typeof value)[K]) =>
      onChange({...value, [key]: next});

  return (
    <VStack spacing={2}>
      <SeparatorWithLabel label={i18n.t('entity.filterForm.savedFilters.dialog.properties')} />

      <ThreeWayPropertyChips
        label={i18n.t('entity.filterForm.featureTiers')}
        value={value.trimLevel}
        onChange={set('trimLevel')}
        sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.trimSameAndBetter')}
        isReadOnly={isReadOnly}
      />

      <Separator spacing={1} />

      <ThreeWayPropertyChips
        label={i18n.t('entity.filterForm.transmission')}
        value={value.transmission}
        onChange={set('transmission') as (v: RelativeFilterChoice | null) => void}
        sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.transmissionSameAndBetter')}
        isReadOnly={isReadOnly}
      />

      <Separator spacing={1} />

      <TwoWayPropertyChips
        label={i18n.t('entity.filterForm.fuel')}
        value={value.fuel as 'same' | null}
        onChange={set('fuel') as (v: 'same' | null) => void}
        isReadOnly={isReadOnly}
      />

      <Separator spacing={1} />

      <TwoWayPropertyChips
        label={i18n.t('entity.filterForm.body')}
        value={value.body as 'same' | null}
        onChange={set('body') as (v: 'same' | null) => void}
        isReadOnly={isReadOnly}
      />

      <Separator spacing={1} />

      <ThreeWayPropertyChips
        label={i18n.t('entity.filterForm.propulsion')}
        value={value.drive}
        onChange={set('drive') as (v: RelativeFilterChoice | null) => void}
        sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.driveSameAndBetter')}
        isReadOnly={isReadOnly}
      />

      <Separator spacing={1} />

      <ThreeWayPropertyChips
        label={i18n.t('entity.filterForm.VATDeductible')}
        value={value.vatDeductible}
        onChange={set('vatDeductible') as (v: RelativeFilterChoice | null) => void}
        sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.vatSameAndBetter')}
        isReadOnly={isReadOnly}
      />

      {/* TODO: seller type hidden until we know the vehicle's seller type
      <Separator spacing={1} />

      <TwoWayPropertyChips
        label={i18n.t('entity.table.sellerType')}
        value={value.sellerType as 'same' | null}
        onChange={set('sellerType') as (v: 'same' | null) => void}
        isReadOnly={isReadOnly}
      />
      */}

      <Separator spacing={1} />

      <ThreeWayPropertyChips
        label={i18n.t('entity.filterForm.seats')}
        value={value.seats}
        onChange={set('seats') as (v: RelativeFilterChoice | null) => void}
        isReadOnly={isReadOnly}
      />
    </VStack>
  );
}
